/**
 * API Client
 *
 * Centralized Axios instance for all backend communications.
 */

import axios, { type AxiosInstance } from 'axios';
import { IS_MOCK } from '../auth/useAuth';
import { IMPERSONATE_KEY } from '../auth/ImpersonateContext';

// API base URL from environment or default to localhost
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001';

/**
 * Axios instance with default configuration
 * - Base URL from environment variables
 * - 2-minute timeout for AI analysis operations
 * - JSON content type by default
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 2 minutes for AI analysis
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor — attach Bearer token and optional impersonation header
apiClient.interceptors.request.use(async (config) => {
  if (IS_MOCK) {
    const stored = sessionStorage.getItem('beacon_mock_user');
    const userId = stored
      ? (JSON.parse(stored) as { allowedUserId: string }).allowedUserId
      : 'mock-dev-token';
    config.headers.Authorization = `Bearer ${userId}`;
  } else {
    const { getMsalToken } = await import('../auth/useRealAuth');
    const token = await getMsalToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }

  // Attach impersonation header when active (read directly from sessionStorage)
  const impersonateStored = sessionStorage.getItem(IMPERSONATE_KEY);
  if (impersonateStored) {
    const { allowedUserId } = JSON.parse(impersonateStored) as { allowedUserId: string };
    config.headers['X-Impersonate-User-Id'] = allowedUserId;
  }

  return config;
});

// Response interceptor — log errors and propagate
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);
